import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from '@/ui';

import { NON_CANCELLED_SESSION_STATUSES } from '../HideCancelledToggle';
import ScheduleSessionCard from '../ScheduleSessionCard';
import { dayKeyFromIso, formatDateKey } from '../calendarGrid';
import { recordCanCancel, recordCanReschedule } from '../recordActionGate';
import { flattenSessionGroups } from '../sessionData';
import {
  ADMIN_NATIVE_LOAD_ERROR_MESSAGE,
  platformAdminAppointmentWindow,
} from '../useCalendarWindow';
import { useSessionActions } from '../useSessionActions';

import SelectedPairListView from './SelectedPairListView';

import type { ScheduleViewProps } from '../types';
import type { AppointmentActionRecord } from '@/features/appointments/appointmentActionRecord';
import type { Session } from '@/types';
import type { FC } from 'react';

import {
  actionRecordEndAt,
  actionRecordIsConfirmed,
  actionRecordKey,
  actionRecordSessionSubject,
  actionRecordStartAt,
  actionRecordSubject,
  isNativeAppointmentActionRecord,
  reconcileCalendarAppointmentActionRecords,
} from '@/features/appointments/appointmentActionRecord';
import { platformAdminCalendarAppointmentRecord } from '@/features/appointments/calendarAppointmentProjection';
import { useGetMomentsCoachWideAppointmentsQuery } from '@/store/api/momentsAppointmentApi';
import { useGetSessionsByCoachQuery } from '@/store/api/sessionApi';
import { useAppDispatch } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';

const PAGE_SIZE = 15;
const TODAY_ANCHOR_ID = 'schedule-today-anchor';
const MENU_ITEM_HEIGHT_PX = 41;
const MENU_VERTICAL_PADDING_PX = 16;
const PARTICIPANT_NATIVE_LOAD_ERROR_MESSAGE = 'Unable to load appointments.';
const NATIVE_LIST_HISTORY_START = '2000-01-01';

const dayKey = (record: AppointmentActionRecord, timeZone?: string | null) =>
  dayKeyFromIso(actionRecordStartAt(record), timeZone);

const startOfDayIso = (dateKey: string, timeZone?: string | null): string => {
  if (!timeZone) return dateKey;

  const [year = 0, month = 1, day = 1] = dateKey.split('-').map(Number);
  const target = Date.UTC(year, month - 1, day);
  const formatter = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
    minute: '2-digit',
    month: '2-digit',
    second: '2-digit',
    timeZone,
    year: 'numeric',
  });
  let instant = target;

  // Converge from UTC midnight to midnight in the calendar's timezone. A
  // second pass handles an offset change between the initial guess and target.
  for (let pass = 0; pass < 2; pass += 1) {
    const parts = formatter.formatToParts(new Date(instant));
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value ?? 0);
    const represented = Date.UTC(
      value('year'),
      value('month') - 1,
      value('day'),
      value('hour'),
      value('minute'),
      value('second'),
    );
    instant += target - represented;
  }

  const dateKeyAt = (timestamp: number) =>
    dayKeyFromIso(new Date(timestamp).toISOString(), timeZone);
  const hour = 60 * 60 * 1000;
  let lower: number;
  let upper: number;

  if (dateKeyAt(instant) < dateKey) {
    lower = instant;
    upper = instant + hour;
    while (dateKeyAt(upper) < dateKey) upper += hour;
  } else {
    upper = instant;
    lower = instant - hour;
    while (dateKeyAt(lower) >= dateKey) lower -= hour;
  }

  // Some zones advance at midnight. Find the first valid instant on or after
  // the requested local date instead of returning the preceding local day.
  while (upper - lower > 1) {
    const midpoint = Math.floor((lower + upper) / 2);
    if (dateKeyAt(midpoint) < dateKey) lower = midpoint;
    else upper = midpoint;
  }

  return new Date(upper).toISOString();
};

const dayHeader = (isoDate: string, todayKey: string): string => {
  if (isoDate === todayKey) return `Today · ${formatDateKey(isoDate)}`;
  return formatDateKey(isoDate, { weekday: true });
};

interface DayGroup {
  key: string;
  header: string;
  isToday: boolean;
  records: AppointmentActionRecord[];
}

interface SessionPage {
  items: Record<string, Session[]>;
  pages?: number;
  total?: number;
}

interface SessionStreams {
  future: Session[];
  futurePages: number;
  past: Session[];
  pastPages: number;
  scopeKey: string;
}

interface SessionQueryState {
  currentData?: SessionPage;
  data?: SessionPage;
  isFetching?: boolean;
  isLoading?: boolean;
}

const emptyStreams = (scopeKey: string): SessionStreams => ({
  future: [],
  futurePages: 1,
  past: [],
  pastPages: 1,
  scopeKey,
});

const dedupeSessions = (sessions: Session[]): Session[] => {
  const seen = new Set<string>();
  return sessions.filter((session) => {
    if (seen.has(session.id)) return false;
    seen.add(session.id);
    return true;
  });
};

const safeQueryData = (query: SessionQueryState): SessionPage | undefined =>
  query.currentData ??
  (!query.isFetching && !query.isLoading ? query.data : undefined);

const hasAnySessions = (...groups: AppointmentActionRecord[][]): boolean =>
  groups.some((sessions) => sessions.length > 0);

const isInitialQueryLoading = (
  query: SessionQueryState,
  sessions: Session[],
  data?: SessionPage,
): boolean =>
  Boolean(
    (query.isLoading || query.isFetching) && sessions.length === 0 && !data,
  );

const isMenuReadOnly = (
  record: AppointmentActionRecord | null,
  adminMode: boolean,
): boolean => {
  if (!record) return false;
  return (
    new Date(actionRecordEndAt(record)).getTime() < Date.now() ||
    (!recordCanReschedule(record, adminMode) &&
      !recordCanCancel(record, adminMode))
  );
};

const hasSessionMenuActions = (
  record: AppointmentActionRecord,
  adminMode: boolean,
): boolean =>
  isMenuReadOnly(record, adminMode)
    ? Boolean(
        actionRecordSessionSubject(record).user.id ||
        actionRecordSessionSubject(record).user.cometChatId,
      )
    : recordCanReschedule(record, adminMode) ||
      recordCanCancel(record, adminMode);

const sessionMenuItemCount = (
  session: Session | null,
  readOnly: boolean,
  canReschedule: boolean,
  canCancel: boolean,
): number =>
  readOnly
    ? Number(Boolean(session?.user.id)) +
      Number(Boolean(session?.user.cometChatId))
    : Number(canReschedule) + Number(canCancel);

const groupByDay = (
  records: AppointmentActionRecord[],
  timeZone?: string | null,
): DayGroup[] => {
  const todayKey = dayKeyFromIso(new Date().toISOString(), timeZone);
  const seen = new Set<string>();
  const deduped = records.filter((record) => {
    const key = actionRecordKey(record);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const byDay = new Map<string, AppointmentActionRecord[]>();
  deduped.forEach((record) => {
    const key = dayKey(record, timeZone);
    const bucket = byDay.get(key) ?? [];
    bucket.push(record);
    byDay.set(key, bucket);
  });

  return [...byDay.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, dayRecords]) => ({
      header: dayHeader(key, todayKey),
      isToday: key === todayKey,
      key,
      records: dayRecords,
    }));
};

// List view (MEN-4160): a single Today-anchored stream. Upcoming sessions load
// forward (asc) and past sessions load backward (desc); both are flattened from
// the month-grouped API response and merged, with Today rendered as an anchor.
const SessionOnlyListView: FC<ScheduleViewProps> = ({
  adminCoachId,
  coachId,
  hideCancelled = false,
  selectedMemberId,
  timeZone,
}) => {
  const [futurePage, setFuturePage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const anchor = useMemo(
    () => dayKeyFromIso(new Date().toISOString(), timeZone),
    [timeZone],
  );
  const queryBoundary = useMemo(
    () => startOfDayIso(anchor, timeZone),
    [anchor, timeZone],
  );
  const scopeKey = `${coachId}:${selectedMemberId ?? 'all'}:${queryBoundary}:${hideCancelled ? 'hide' : 'show'}-cancelled`;
  // MEN-5349: filter on the server so Session pages stay full.
  const statuses = hideCancelled ? NON_CANCELLED_SESSION_STATUSES : undefined;
  const [streams, setStreams] = useState<SessionStreams>(() =>
    emptyStreams(scopeKey),
  );
  const pastSentinelRef = useRef<HTMLDivElement | null>(null);
  const futureSentinelRef = useRef<HTMLDivElement | null>(null);
  const scrolledScopeRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const adminMode = Boolean(adminCoachId);
  const nativeFutureWindow = useMemo(
    () => platformAdminAppointmentWindow(timeZone),
    [timeZone],
  );
  const nativeHistoryStart = useMemo(
    () => startOfDayIso(NATIVE_LIST_HISTORY_START, timeZone),
    [timeZone],
  );
  const nativeCoach = useMemo(
    () => ({
      externalId: adminCoachId ?? coachId,
      provider: 'legacy_app' as const,
    }),
    [adminCoachId, coachId],
  );
  const futureAppointments = useGetMomentsCoachWideAppointmentsQuery({
    coach: nativeCoach,
    limit: 100,
    rangeEnd: nativeFutureWindow.rangeEnd,
    rangeStart: queryBoundary,
  });
  const pastAppointments = useGetMomentsCoachWideAppointmentsQuery({
    coach: nativeCoach,
    limit: 100,
    rangeEnd: queryBoundary,
    rangeStart: nativeHistoryStart,
  });
  const appointments = useMemo(
    () => [
      ...(pastAppointments.currentData?.appointments ?? []),
      ...(futureAppointments.currentData?.appointments ?? []),
    ],
    [
      futureAppointments.currentData?.appointments,
      pastAppointments.currentData?.appointments,
    ],
  );
  const adminNativeLoadError = Boolean(
    adminCoachId && (futureAppointments.isError || pastAppointments.isError),
  );
  const participantNativeLoadError = Boolean(
    !adminCoachId && (futureAppointments.isError || pastAppointments.isError),
  );
  const nativeLoadError = adminNativeLoadError || participantNativeLoadError;
  const participantAuthorityPending = Boolean(
    !adminCoachId &&
    (futureAppointments.currentData || pastAppointments.currentData) &&
    (futureAppointments.isFetching || pastAppointments.isFetching),
  );
  const actionControlsDisabled = nativeLoadError || participantAuthorityPending;
  const actions = useSessionActions(timeZone, {
    adminCoachId,
    disabled: nativeLoadError,
  });

  const future = useGetSessionsByCoachQuery({
    actingRole: adminMode ? undefined : 'coach',
    coachId,
    limit: PAGE_SIZE,
    minDate: queryBoundary,
    order: 'asc',
    page: futurePage,
    statuses,
    userId: selectedMemberId ?? undefined,
  });
  const past = useGetSessionsByCoachQuery({
    actingRole: adminMode ? undefined : 'coach',
    coachId,
    limit: PAGE_SIZE,
    maxDate: queryBoundary,
    order: 'desc',
    page: pastPage,
    statuses,
    userId: selectedMemberId ?? undefined,
  });

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeRecord, setActiveRecord] =
    useState<AppointmentActionRecord | null>(null);

  useEffect(() => {
    if (!actionControlsDisabled) return;
    setMenuAnchor(null);
    setActiveRecord(null);
  }, [actionControlsDisabled]);

  useEffect(() => {
    setFuturePage(1);
    setPastPage(1);
    setStreams(emptyStreams(scopeKey));
    setMenuAnchor(null);
    setActiveRecord(null);
  }, [scopeKey]);

  const futureData = safeQueryData(future);
  const pastData = safeQueryData(past);

  useEffect(() => {
    if (!futureData) return;
    const pageSessions = flattenSessionGroups(futureData.items).filter(
      (session) => dayKeyFromIso(session.startedAt, timeZone) >= anchor,
    );
    setStreams((current) => {
      const base =
        current.scopeKey === scopeKey ? current : emptyStreams(scopeKey);
      return {
        ...base,
        future: dedupeSessions(
          futurePage === 1 ? pageSessions : [...base.future, ...pageSessions],
        ),
        futurePages:
          futureData.pages ??
          Math.max(
            1,
            Math.ceil((futureData.total ?? pageSessions.length) / PAGE_SIZE),
          ),
      };
    });
  }, [anchor, futureData, futurePage, scopeKey, timeZone]);

  useEffect(() => {
    if (!pastData) return;
    const pageSessions = flattenSessionGroups(pastData.items).filter(
      (session) => dayKeyFromIso(session.startedAt, timeZone) < anchor,
    );
    setStreams((current) => {
      const base =
        current.scopeKey === scopeKey ? current : emptyStreams(scopeKey);
      return {
        ...base,
        past: dedupeSessions(
          pastPage === 1 ? pageSessions : [...base.past, ...pageSessions],
        ),
        pastPages:
          pastData.pages ??
          Math.max(
            1,
            Math.ceil((pastData.total ?? pageSessions.length) / PAGE_SIZE),
          ),
      };
    });
  }, [anchor, pastData, pastPage, scopeKey, timeZone]);

  const scopedStreams =
    streams.scopeKey === scopeKey ? streams : emptyStreams(scopeKey);
  const firstFuturePageSessions = useMemo(
    () =>
      futurePage === 1 && futureData
        ? flattenSessionGroups(futureData.items).filter(
            (session) => dayKeyFromIso(session.startedAt, timeZone) >= anchor,
          )
        : [],
    [anchor, futureData, futurePage, timeZone],
  );
  const firstPastPageSessions = useMemo(
    () =>
      pastPage === 1 && pastData
        ? flattenSessionGroups(pastData.items).filter(
            (session) => dayKeyFromIso(session.startedAt, timeZone) < anchor,
          )
        : [],
    [anchor, pastData, pastPage, timeZone],
  );
  const futureSessions =
    scopedStreams.future.length > 0
      ? scopedStreams.future
      : firstFuturePageSessions;
  const pastSessions =
    scopedStreams.past.length > 0 ? scopedStreams.past : firstPastPageSessions;

  const listRecords = useMemo(() => {
    if (nativeLoadError) return [];
    const sessions = [...pastSessions, ...futureSessions];
    const records = reconcileCalendarAppointmentActionRecords(
      appointments.map(platformAdminCalendarAppointmentRecord),
      sessions,
    );
    return records.filter((record) => {
      if (!actionRecordIsConfirmed(record)) return false;
      if (!isNativeAppointmentActionRecord(record)) {
        return !hideCancelled || record.status !== 'cancelled';
      }
      return (
        (adminCoachId && !hideCancelled) ||
        record.view.lifecycleStatus !== 'cancelled'
      );
    });
  }, [
    adminCoachId,
    appointments,
    futureSessions,
    hideCancelled,
    nativeLoadError,
    pastSessions,
  ]);
  const pastRecords = useMemo(
    () =>
      listRecords.filter(
        (record) =>
          dayKeyFromIso(actionRecordStartAt(record), timeZone) < anchor,
      ),
    [anchor, listRecords, timeZone],
  );
  const futureRecords = useMemo(
    () =>
      listRecords.filter(
        (record) =>
          dayKeyFromIso(actionRecordStartAt(record), timeZone) >= anchor,
      ),
    [anchor, listRecords, timeZone],
  );
  const pastGroups = useMemo(
    () => groupByDay(pastRecords, timeZone),
    [pastRecords, timeZone],
  );
  const todayRecords = useMemo(
    () =>
      futureRecords.filter(
        (record) =>
          dayKeyFromIso(actionRecordStartAt(record), timeZone) === anchor,
      ),
    [anchor, futureRecords, timeZone],
  );
  const futureGroups = useMemo(
    () =>
      groupByDay(
        futureRecords.filter(
          (record) =>
            dayKeyFromIso(actionRecordStartAt(record), timeZone) > anchor,
        ),
        timeZone,
      ),
    [anchor, futureRecords, timeZone],
  );

  const sessionActionsMenuId = 'schedule-session-actions-menu';
  const openMenu = (record: AppointmentActionRecord, element: HTMLElement) => {
    setActiveRecord(record);
    setMenuAnchor(element);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setActiveRecord(null);
  };

  // A session is "past" only once it has ended; in-progress sessions remain
  // reschedulable/cancelable.
  const activeSession = activeRecord
    ? actionRecordSessionSubject(activeRecord)
    : null;
  const activeIsReadOnly =
    actionControlsDisabled || isMenuReadOnly(activeRecord, adminMode);
  const activeCanReschedule = activeRecord
    ? !actionControlsDisabled && recordCanReschedule(activeRecord, adminMode)
    : false;
  const activeCanCancel = activeRecord
    ? !actionControlsDisabled && recordCanCancel(activeRecord, adminMode)
    : false;
  const activeMenuItemCount = sessionMenuItemCount(
    activeSession,
    activeIsReadOnly,
    activeCanReschedule,
    activeCanCancel,
  );
  const menuHeight =
    MENU_VERTICAL_PADDING_PX + MENU_ITEM_HEIGHT_PX * activeMenuItemCount;

  const hasMorePast = pastPage < scopedStreams.pastPages;
  const hasMoreFuture = futurePage < scopedStreams.futurePages;
  const hasSessions = hasAnySessions(pastRecords, todayRecords, futureRecords);
  const isInitialLoading =
    isInitialQueryLoading(future, futureSessions, futureData) ||
    isInitialQueryLoading(past, pastSessions, pastData) ||
    Boolean(
      appointments.length === 0 &&
      (futureAppointments.isLoading ||
        futureAppointments.isFetching ||
        pastAppointments.isLoading ||
        pastAppointments.isFetching),
    );

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (
          entry.target === pastSentinelRef.current &&
          hasMorePast &&
          !past.isFetching
        ) {
          setPastPage((page) => Math.min(page + 1, scopedStreams.pastPages));
        }
        if (
          entry.target === futureSentinelRef.current &&
          hasMoreFuture &&
          !future.isFetching
        ) {
          setFuturePage((page) =>
            Math.min(page + 1, scopedStreams.futurePages),
          );
        }
      });
    });

    const pastNode = pastSentinelRef.current;
    const futureNode = futureSentinelRef.current;
    if (pastNode) observer.observe(pastNode);
    if (futureNode) observer.observe(futureNode);
    return () => observer.disconnect();
  }, [
    future.isFetching,
    hasMoreFuture,
    hasMorePast,
    past.isFetching,
    scopedStreams.futurePages,
    scopedStreams.pastPages,
  ]);

  useEffect(() => {
    if (isInitialLoading || scrolledScopeRef.current === scopeKey) return;
    const todayAnchor = document.getElementById(TODAY_ANCHOR_ID);
    if (typeof todayAnchor?.scrollIntoView === 'function') {
      todayAnchor.scrollIntoView({ block: 'start' });
    }
    scrolledScopeRef.current = scopeKey;
  }, [isInitialLoading, scopeKey]);

  if (isInitialLoading) {
    return <CircularProgress data-testid="schedule-list-loading" />;
  }
  if (adminNativeLoadError) {
    return <Alert severity="error">{ADMIN_NATIVE_LOAD_ERROR_MESSAGE}</Alert>;
  }
  if (participantNativeLoadError) {
    return (
      <Alert
        action={
          <Button
            color="inherit"
            onClick={() => {
              pastAppointments.refetch();
              futureAppointments.refetch();
            }}
          >
            Retry
          </Button>
        }
        severity="error"
      >
        {PARTICIPANT_NATIVE_LOAD_ERROR_MESSAGE}
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="schedule-list-view">
      <div ref={pastSentinelRef} data-testid="schedule-list-past-sentinel" />

      {pastGroups.map((group) => (
        <section
          key={group.key}
          className="grid gap-4"
          style={{ gridTemplateColumns: '7.5rem minmax(0, 1fr)' }}
        >
          <div className="py-3" data-testid="schedule-list-date-gutter">
            <Typography variant="subtitle2">{group.header}</Typography>
          </div>
          <div
            className="flex min-w-0 flex-col gap-2"
            data-testid="schedule-list-card-column"
          >
            {group.records.map((record) => {
              const session = actionRecordSessionSubject(record);
              return (
                <ScheduleSessionCard
                  key={actionRecordKey(record)}
                  hasOptions={
                    actionControlsDisabled
                      ? Boolean(session.user.id || session.user.cometChatId)
                      : hasSessionMenuActions(record, adminMode)
                  }
                  menuId={sessionActionsMenuId}
                  menuOpen={
                    activeRecord
                      ? actionRecordKey(activeRecord) ===
                        actionRecordKey(record)
                      : false
                  }
                  session={session}
                  timeZone={timeZone}
                  onOpenMenu={(_session, element) => openMenu(record, element)}
                />
              );
            })}
          </div>
        </section>
      ))}

      {todayRecords.length > 0 ? (
        <section
          className="grid gap-4"
          id={TODAY_ANCHOR_ID}
          style={{ gridTemplateColumns: '7.5rem minmax(0, 1fr)' }}
        >
          <div className="py-3" data-testid="schedule-list-date-gutter">
            <Typography variant="subtitle2">
              {dayHeader(anchor, anchor)}
            </Typography>
          </div>
          <div
            className="flex min-w-0 flex-col gap-2"
            data-testid="schedule-list-card-column"
          >
            {todayRecords.map((record) => {
              const session = actionRecordSessionSubject(record);
              return (
                <ScheduleSessionCard
                  key={actionRecordKey(record)}
                  hasOptions={
                    actionControlsDisabled
                      ? Boolean(session.user.id || session.user.cometChatId)
                      : hasSessionMenuActions(record, adminMode)
                  }
                  menuId={sessionActionsMenuId}
                  menuOpen={
                    activeRecord
                      ? actionRecordKey(activeRecord) ===
                        actionRecordKey(record)
                      : false
                  }
                  session={session}
                  timeZone={timeZone}
                  onOpenMenu={(_session, element) => openMenu(record, element)}
                />
              );
            })}
          </div>
        </section>
      ) : (
        <div id={TODAY_ANCHOR_ID} />
      )}

      {!hasSessions && (
        <Typography color="textSecondary" variant="body2">
          No sessions to show.
        </Typography>
      )}

      {futureGroups.map((group) => (
        <section
          key={group.key}
          className="grid gap-4"
          style={{ gridTemplateColumns: '7.5rem minmax(0, 1fr)' }}
        >
          <div className="py-3" data-testid="schedule-list-date-gutter">
            <Typography variant="subtitle2">{group.header}</Typography>
          </div>
          <div
            className="flex min-w-0 flex-col gap-2"
            data-testid="schedule-list-card-column"
          >
            {group.records.map((record) => {
              const session = actionRecordSessionSubject(record);
              return (
                <ScheduleSessionCard
                  key={actionRecordKey(record)}
                  hasOptions={
                    actionControlsDisabled
                      ? Boolean(session.user.id || session.user.cometChatId)
                      : hasSessionMenuActions(record, adminMode)
                  }
                  menuId={sessionActionsMenuId}
                  menuOpen={
                    activeRecord
                      ? actionRecordKey(activeRecord) ===
                        actionRecordKey(record)
                      : false
                  }
                  session={session}
                  timeZone={timeZone}
                  onOpenMenu={(_session, element) => openMenu(record, element)}
                />
              );
            })}
          </div>
        </section>
      ))}

      <div
        ref={futureSentinelRef}
        data-testid="schedule-list-future-sentinel"
      />

      <Menu
        anchorEl={menuAnchor}
        MenuListProps={{ id: sessionActionsMenuId }}
        open={Boolean(menuAnchor)}
        PaperProps={{ style: { height: `${menuHeight}px`, width: '155px' } }}
        onClose={closeMenu}
      >
        {activeIsReadOnly
          ? [
              activeSession?.user.id ? (
                <MenuItem
                  key="profile"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    navigate(`/coach/members/${activeSession.user.id}`);
                    closeMenu();
                  }}
                >
                  View Profile
                </MenuItem>
              ) : null,
              activeSession?.user.cometChatId ? (
                <MenuItem
                  key="message"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    dispatch(setChatId(activeSession.user.cometChatId));
                    closeMenu();
                  }}
                >
                  {`Message ${activeSession.user.fullName}`.trim()}
                </MenuItem>
              ) : null,
            ]
          : [
              activeCanReschedule ? (
                <MenuItem
                  key="reschedule"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    const focusTarget = menuAnchor;
                    if (activeRecord) {
                      actions.openReschedule(
                        actionRecordSubject(activeRecord),
                        () => focusTarget?.focus(),
                      );
                    }
                    closeMenu();
                  }}
                >
                  Reschedule
                </MenuItem>
              ) : null,
              activeCanCancel ? (
                <MenuItem
                  key="cancel"
                  style={{ height: MENU_ITEM_HEIGHT_PX, minHeight: 0 }}
                  onClick={() => {
                    if (activeRecord) {
                      const focusTarget = menuAnchor;
                      actions
                        .openCancel(actionRecordSubject(activeRecord), () =>
                          focusTarget?.focus(),
                        )
                        .catch(() => undefined);
                    }
                    closeMenu();
                  }}
                >
                  Cancel
                </MenuItem>
              ) : null,
            ]}
      </Menu>
      {actions.modals}
    </div>
  );
};

const ListView: FC<ScheduleViewProps> = ({
  adminCoachId,
  coachId,
  hideCancelled,
  selectedMemberId,
  timeZone,
  ...props
}) => (
  <>
    {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Keyboard users need to focus and scroll the constrained session stream. */}
    <div
      className="h-full overflow-y-auto"
      data-testid="schedule-list-scroll-region"
      tabIndex={0}
    >
      {selectedMemberId ? (
        <SelectedPairListView
          key={`${coachId}:${selectedMemberId}:${timeZone ?? 'UTC'}`}
          adminCoachId={adminCoachId}
          coachId={coachId}
          hideCancelled={hideCancelled}
          memberId={selectedMemberId}
          timeZone={timeZone}
        />
      ) : (
        <SessionOnlyListView
          {...props}
          adminCoachId={adminCoachId}
          coachId={coachId}
          hideCancelled={hideCancelled}
          selectedMemberId={selectedMemberId}
          timeZone={timeZone}
        />
      )}
    </div>
    {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
  </>
);

export default ListView;
